import {
  Activity,
  Camera,
  Circle,
  Download,
  Eye,
  EyeOff,
  FileBox,
  RefreshCw,
  RotateCcw,
  Square,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaceLandmarker, PoseLandmarker, type NormalizedLandmark } from "@mediapipe/tasks-vision";
import { buildCaptureZip } from "./exportData";
import { createFaceLandmarker, createPoseLandmarker, resultToPoseSample, resultToSample } from "./mediapipe";
import type { CaptureFrame, CaptureSession, CaptureStatus, ExportExtras, PoseSample, TrackingSample } from "./types";

const APP_NAME = "Tony Marghella Face Capture";
const APP_VERSION = "0.1.0";
const LANDMARK_SMOOTHING = 0.64;
const TRACKING_GRACE_MS = 180;
const UI_UPDATE_MS = 120;

type OverlayMode = "mesh" | "landmarks" | "off";
type Point2 = [number, number];

const DEFAULT_EXPORT_EXTRAS: ExportExtras = {
  head: false,
  eyes: false,
  teeth: false,
  tongue: false,
  neck: false,
  torso: false,
  arms: false,
  fullBody: false,
};

const EXPORT_EXTRA_LABELS: Array<{ key: keyof ExportExtras; label: string }> = [
  { key: "head", label: "Head" },
  { key: "eyes", label: "Eyes" },
  { key: "teeth", label: "Teeth" },
  { key: "tongue", label: "Tongue" },
  { key: "neck", label: "Neck" },
  { key: "torso", label: "Torso" },
  { key: "arms", label: "Arms" },
  { key: "fullBody", label: "Full body" },
];

function cloneLandmarks(landmarks: NormalizedLandmark[]): NormalizedLandmark[] {
  return landmarks.map((landmark) => ({
    x: landmark.x,
    y: landmark.y,
    z: landmark.z,
    visibility: landmark.visibility ?? 0,
  }));
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function formatScore(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function averageFps(times: number[]): number {
  if (times.length < 2) return 0;
  const span = times[times.length - 1] - times[0];
  return span > 0 ? ((times.length - 1) / span) * 1000 : 0;
}

function displaySample(previous: TrackingSample | null, current: TrackingSample, smoothingEnabled: boolean): TrackingSample {
  if (!smoothingEnabled || !previous || previous.landmarks.length !== current.landmarks.length) {
    return {
      ...current,
      landmarks: cloneLandmarks(current.landmarks),
    };
  }

  return {
    ...current,
    landmarks: current.landmarks.map((landmark, index) => {
      const prior = previous.landmarks[index];
      return {
        x: prior.x * LANDMARK_SMOOTHING + landmark.x * (1 - LANDMARK_SMOOTHING),
        y: prior.y * LANDMARK_SMOOTHING + landmark.y * (1 - LANDMARK_SMOOTHING),
        z: prior.z * LANDMARK_SMOOTHING + landmark.z * (1 - LANDMARK_SMOOTHING),
        visibility: landmark.visibility ?? prior.visibility ?? 0,
      };
    }),
  };
}

function displayPoseSample(previous: PoseSample | null, current: PoseSample, smoothingEnabled: boolean): PoseSample {
  if (!smoothingEnabled || !previous || previous.landmarks.length !== current.landmarks.length) {
    return {
      landmarks: cloneLandmarks(current.landmarks),
    };
  }

  return {
    landmarks: current.landmarks.map((landmark, index) => {
      const prior = previous.landmarks[index];
      return {
        x: prior.x * LANDMARK_SMOOTHING + landmark.x * (1 - LANDMARK_SMOOTHING),
        y: prior.y * LANDMARK_SMOOTHING + landmark.y * (1 - LANDMARK_SMOOTHING),
        z: prior.z * LANDMARK_SMOOTHING + landmark.z * (1 - LANDMARK_SMOOTHING),
        visibility: landmark.visibility ?? prior.visibility ?? 0,
      };
    }),
  };
}

function needsPoseOverlay(extras: ExportExtras): boolean {
  return extras.neck || extras.torso || extras.arms || extras.fullBody;
}

function pointFor(landmarks: NormalizedLandmark[], index: number, width: number, height: number): Point2 | null {
  const point = landmarks[index];
  if (!point || (point.visibility ?? 1) < 0.28) return null;
  return [point.x * width, point.y * height];
}

function midpoint(a: Point2, b: Point2): Point2 {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

function distance2d(a: Point2, b: Point2): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

function interpolatePoint(a: Point2, b: Point2, amount: number): Point2 {
  return [a[0] + (b[0] - a[0]) * amount, a[1] + (b[1] - a[1]) * amount];
}

function quadPoint(topLeft: Point2, topRight: Point2, bottomRight: Point2, bottomLeft: Point2, u: number, v: number): Point2 {
  const top = interpolatePoint(topLeft, topRight, u);
  const bottom = interpolatePoint(bottomLeft, bottomRight, u);
  return interpolatePoint(top, bottom, v);
}

function drawConnections(
  context: CanvasRenderingContext2D,
  landmarks: NormalizedLandmark[],
  connections: Array<{ start: number; end: number }>,
  width: number,
  height: number,
  color: string,
  lineWidth: number,
) {
  context.save();
  context.strokeStyle = color;
  context.lineWidth = lineWidth;
  context.lineCap = "round";
  context.lineJoin = "round";
  for (const connection of connections) {
    const start = pointFor(landmarks, connection.start, width, height);
    const end = pointFor(landmarks, connection.end, width, height);
    if (!start || !end) continue;
    context.beginPath();
    context.moveTo(start[0], start[1]);
    context.lineTo(end[0], end[1]);
    context.stroke();
  }
  context.restore();
}

function drawEllipse(
  context: CanvasRenderingContext2D,
  center: Point2,
  radiusX: number,
  radiusY: number,
  fillStyle: string,
  strokeStyle: string,
) {
  context.save();
  context.fillStyle = fillStyle;
  context.strokeStyle = strokeStyle;
  context.lineWidth = 1.6;
  context.beginPath();
  context.ellipse(center[0], center[1], radiusX, radiusY, 0, 0, Math.PI * 2);
  context.fill();
  context.stroke();
  context.restore();
}

function drawSurfaceMesh(
  context: CanvasRenderingContext2D,
  corners: [Point2, Point2, Point2, Point2],
  fillStyle: string,
  strokeStyle: string,
  rows = 5,
  columns = 5,
) {
  const [topLeft, topRight, bottomRight, bottomLeft] = corners;
  context.save();
  context.fillStyle = fillStyle;
  context.strokeStyle = strokeStyle;
  context.lineWidth = 1.25;
  context.lineCap = "round";
  context.lineJoin = "round";

  context.beginPath();
  context.moveTo(topLeft[0], topLeft[1]);
  context.lineTo(topRight[0], topRight[1]);
  context.lineTo(bottomRight[0], bottomRight[1]);
  context.lineTo(bottomLeft[0], bottomLeft[1]);
  context.closePath();
  context.fill();
  context.stroke();

  context.globalAlpha = 0.72;
  for (let row = 1; row < rows; row += 1) {
    const v = row / rows;
    const left = quadPoint(topLeft, topRight, bottomRight, bottomLeft, 0, v);
    const right = quadPoint(topLeft, topRight, bottomRight, bottomLeft, 1, v);
    context.beginPath();
    context.moveTo(left[0], left[1]);
    context.lineTo(right[0], right[1]);
    context.stroke();
  }

  for (let column = 1; column < columns; column += 1) {
    const u = column / columns;
    const top = quadPoint(topLeft, topRight, bottomRight, bottomLeft, u, 0);
    const bottom = quadPoint(topLeft, topRight, bottomRight, bottomLeft, u, 1);
    context.beginPath();
    context.moveTo(top[0], top[1]);
    context.lineTo(bottom[0], bottom[1]);
    context.stroke();
  }

  context.globalAlpha = 0.42;
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const a = quadPoint(topLeft, topRight, bottomRight, bottomLeft, column / columns, row / rows);
      const b = quadPoint(topLeft, topRight, bottomRight, bottomLeft, (column + 1) / columns, (row + 1) / rows);
      context.beginPath();
      context.moveTo(a[0], a[1]);
      context.lineTo(b[0], b[1]);
      context.stroke();
    }
  }

  context.restore();
}

function drawLimbMesh(
  context: CanvasRenderingContext2D,
  start: Point2,
  end: Point2,
  startRadius: number,
  endRadius: number,
  fillStyle: string,
  strokeStyle: string,
  segments = 5,
) {
  const length = distance2d(start, end);
  if (length < 4) return;

  const normal: Point2 = [-(end[1] - start[1]) / length, (end[0] - start[0]) / length];
  const left: Point2[] = [];
  const right: Point2[] = [];

  for (let index = 0; index <= segments; index += 1) {
    const amount = index / segments;
    const center = interpolatePoint(start, end, amount);
    const radius = startRadius + (endRadius - startRadius) * amount;
    left.push([center[0] + normal[0] * radius, center[1] + normal[1] * radius]);
    right.push([center[0] - normal[0] * radius, center[1] - normal[1] * radius]);
  }

  context.save();
  context.fillStyle = fillStyle;
  context.strokeStyle = strokeStyle;
  context.lineWidth = 1.2;
  context.lineCap = "round";
  context.lineJoin = "round";

  context.beginPath();
  context.moveTo(left[0][0], left[0][1]);
  left.slice(1).forEach((point) => context.lineTo(point[0], point[1]));
  right.slice().reverse().forEach((point) => context.lineTo(point[0], point[1]));
  context.closePath();
  context.fill();
  context.stroke();

  context.globalAlpha = 0.74;
  for (let index = 1; index < left.length; index += 1) {
    context.beginPath();
    context.moveTo(left[index][0], left[index][1]);
    context.lineTo(right[index][0], right[index][1]);
    context.stroke();

    context.globalAlpha = 0.42;
    context.beginPath();
    context.moveTo(left[index - 1][0], left[index - 1][1]);
    context.lineTo(right[index][0], right[index][1]);
    context.moveTo(right[index - 1][0], right[index - 1][1]);
    context.lineTo(left[index][0], left[index][1]);
    context.stroke();
    context.globalAlpha = 0.74;
  }

  context.globalAlpha = 0.92;
  context.beginPath();
  context.moveTo(start[0], start[1]);
  context.lineTo(end[0], end[1]);
  context.stroke();
  context.restore();
}

function drawGuide(context: CanvasRenderingContext2D, width: number, height: number) {
  context.save();
  context.clearRect(0, 0, width, height);
  context.strokeStyle = "rgba(226, 240, 236, 0.34)";
  context.lineWidth = Math.max(2, width * 0.004);
  context.setLineDash([12, 16]);
  context.beginPath();
  context.ellipse(width * 0.5, height * 0.48, width * 0.2, height * 0.31, 0, 0, Math.PI * 2);
  context.stroke();
  context.restore();
}

function drawFaceExtras(
  context: CanvasRenderingContext2D,
  points: Array<[number, number]>,
  extras: ExportExtras,
) {
  if (extras.head) {
    context.save();
    context.lineWidth = 2.4;
    context.strokeStyle = "rgba(90, 166, 255, 0.82)";
    for (const connection of FaceLandmarker.FACE_LANDMARKS_FACE_OVAL) {
      const start = points[connection.start];
      const end = points[connection.end];
      if (!start || !end) continue;
      context.beginPath();
      context.moveTo(start[0], start[1]);
      context.lineTo(end[0], end[1]);
      context.stroke();
    }
    context.restore();
  }

  if (extras.eyes) {
    context.save();
    context.lineWidth = 2.1;
    context.strokeStyle = "rgba(230, 245, 255, 0.92)";
    for (const connection of [
      ...FaceLandmarker.FACE_LANDMARKS_LEFT_EYE,
      ...FaceLandmarker.FACE_LANDMARKS_RIGHT_EYE,
      ...FaceLandmarker.FACE_LANDMARKS_LEFT_IRIS,
      ...FaceLandmarker.FACE_LANDMARKS_RIGHT_IRIS,
    ]) {
      const start = points[connection.start];
      const end = points[connection.end];
      if (!start || !end) continue;
      context.beginPath();
      context.moveTo(start[0], start[1]);
      context.lineTo(end[0], end[1]);
      context.stroke();
    }
    context.restore();
  }

  const mouthLeft = points[61];
  const mouthRight = points[291];
  const upperLip = points[13];
  const lowerLip = points[14];
  if (mouthLeft && mouthRight && upperLip && lowerLip) {
    const mouthCenter = midpoint(mouthLeft, mouthRight);
    mouthCenter[1] = (upperLip[1] + lowerLip[1]) / 2;
    const mouthWidth = Math.max(18, Math.hypot(mouthRight[0] - mouthLeft[0], mouthRight[1] - mouthLeft[1]));
    const rawMouthHeight = Math.abs(lowerLip[1] - upperLip[1]);
    const mouthHeight = Math.max(8, rawMouthHeight);

    if (extras.teeth) {
      drawEllipse(
        context,
        [mouthCenter[0], mouthCenter[1] - mouthHeight * 0.38],
        mouthWidth * 0.32,
        Math.max(3, mouthHeight * 0.28),
        "rgba(255, 245, 210, 0.36)",
        "rgba(255, 248, 224, 0.9)",
      );
      drawEllipse(
        context,
        [mouthCenter[0], mouthCenter[1] + mouthHeight * 0.38],
        mouthWidth * 0.3,
        Math.max(3, mouthHeight * 0.24),
        "rgba(255, 245, 210, 0.28)",
        "rgba(255, 248, 224, 0.76)",
      );
    }

    if (extras.tongue && rawMouthHeight > Math.max(5, mouthWidth * 0.055)) {
      const tongueCenter: Point2 = [mouthCenter[0], upperLip[1] + rawMouthHeight * 0.76];
      drawEllipse(
        context,
        tongueCenter,
        mouthWidth * 0.18,
        Math.max(3.5, rawMouthHeight * 0.24),
        "rgba(255, 92, 114, 0.24)",
        "rgba(255, 130, 148, 0.72)",
      );
      context.save();
      context.strokeStyle = "rgba(255, 177, 186, 0.42)";
      context.lineWidth = 1;
      context.beginPath();
      context.moveTo(tongueCenter[0] - mouthWidth * 0.08, tongueCenter[1]);
      context.quadraticCurveTo(tongueCenter[0], tongueCenter[1] + rawMouthHeight * 0.09, tongueCenter[0] + mouthWidth * 0.08, tongueCenter[1]);
      context.stroke();
      context.restore();
    }
  }
}

function drawPoseOverlay(
  context: CanvasRenderingContext2D,
  poseSample: PoseSample | null,
  extras: ExportExtras,
  width: number,
  height: number,
) {
  if (!poseSample || !needsPoseOverlay(extras)) return;
  const { landmarks } = poseSample;
  const leftShoulder = pointFor(landmarks, 11, width, height);
  const rightShoulder = pointFor(landmarks, 12, width, height);
  const leftElbow = pointFor(landmarks, 13, width, height);
  const rightElbow = pointFor(landmarks, 14, width, height);
  const leftWrist = pointFor(landmarks, 15, width, height);
  const rightWrist = pointFor(landmarks, 16, width, height);
  const leftHip = pointFor(landmarks, 23, width, height);
  const rightHip = pointFor(landmarks, 24, width, height);
  const leftKnee = pointFor(landmarks, 25, width, height);
  const rightKnee = pointFor(landmarks, 26, width, height);
  const leftAnkle = pointFor(landmarks, 27, width, height);
  const rightAnkle = pointFor(landmarks, 28, width, height);
  const nose = pointFor(landmarks, 0, width, height);
  const shoulderWidth = leftShoulder && rightShoulder ? distance2d(leftShoulder, rightShoulder) : 0;
  const hipWidth = leftHip && rightHip ? distance2d(leftHip, rightHip) : 0;
  const bodyScale = Math.max(18, shoulderWidth || hipWidth || height * 0.18);

  if (extras.fullBody) {
    drawConnections(
      context,
      landmarks,
      PoseLandmarker.POSE_CONNECTIONS,
      width,
      height,
      "rgba(91, 178, 255, 0.22)",
      0.9,
    );
  }

  if ((extras.torso || extras.fullBody) && leftShoulder && rightShoulder && leftHip && rightHip) {
    drawSurfaceMesh(
      context,
      [leftShoulder, rightShoulder, rightHip, leftHip],
      "rgba(91, 178, 255, 0.11)",
      "rgba(91, 178, 255, 0.78)",
      5,
      5,
    );
  }

  if ((extras.neck || extras.fullBody) && leftShoulder && rightShoulder) {
    const shoulderCenter = midpoint(leftShoulder, rightShoulder);
    const neckTop: Point2 = nose
      ? [shoulderCenter[0] * 0.65 + nose[0] * 0.35, shoulderCenter[1] * 0.74 + nose[1] * 0.26]
      : [shoulderCenter[0], shoulderCenter[1] - Math.max(24, height * 0.08)];
    drawLimbMesh(
      context,
      shoulderCenter,
      neckTop,
      bodyScale * 0.085,
      bodyScale * 0.055,
      "rgba(91, 235, 191, 0.1)",
      "rgba(91, 235, 191, 0.8)",
      4,
    );
  }

  if (extras.arms || extras.fullBody) {
    const upperArmRadius = bodyScale * 0.058;
    const forearmRadius = bodyScale * 0.046;
    if (leftShoulder && leftElbow) {
      drawLimbMesh(context, leftShoulder, leftElbow, upperArmRadius, forearmRadius, "rgba(171, 132, 255, 0.1)", "rgba(171, 132, 255, 0.82)");
    }
    if (leftElbow && leftWrist) {
      drawLimbMesh(context, leftElbow, leftWrist, forearmRadius, forearmRadius * 0.72, "rgba(171, 132, 255, 0.09)", "rgba(171, 132, 255, 0.78)");
    }
    if (rightShoulder && rightElbow) {
      drawLimbMesh(context, rightShoulder, rightElbow, upperArmRadius, forearmRadius, "rgba(171, 132, 255, 0.1)", "rgba(171, 132, 255, 0.82)");
    }
    if (rightElbow && rightWrist) {
      drawLimbMesh(context, rightElbow, rightWrist, forearmRadius, forearmRadius * 0.72, "rgba(171, 132, 255, 0.09)", "rgba(171, 132, 255, 0.78)");
    }
  }

  if (extras.fullBody) {
    const thighRadius = bodyScale * 0.068;
    const shinRadius = bodyScale * 0.052;
    if (leftHip && leftKnee) {
      drawLimbMesh(context, leftHip, leftKnee, thighRadius, shinRadius, "rgba(91, 178, 255, 0.09)", "rgba(91, 178, 255, 0.72)");
    }
    if (leftKnee && leftAnkle) {
      drawLimbMesh(context, leftKnee, leftAnkle, shinRadius, shinRadius * 0.66, "rgba(91, 178, 255, 0.08)", "rgba(91, 178, 255, 0.66)");
    }
    if (rightHip && rightKnee) {
      drawLimbMesh(context, rightHip, rightKnee, thighRadius, shinRadius, "rgba(91, 178, 255, 0.09)", "rgba(91, 178, 255, 0.72)");
    }
    if (rightKnee && rightAnkle) {
      drawLimbMesh(context, rightKnee, rightAnkle, shinRadius, shinRadius * 0.66, "rgba(91, 178, 255, 0.08)", "rgba(91, 178, 255, 0.66)");
    }

    if (leftHip && rightHip && leftKnee && rightKnee) {
      drawSurfaceMesh(
        context,
        [leftHip, rightHip, rightKnee, leftKnee],
        "rgba(91, 178, 255, 0.055)",
        "rgba(91, 178, 255, 0.38)",
        3,
        3,
      );
    }
  }

  context.save();
  context.fillStyle = "rgba(244, 216, 122, 0.86)";
  const landmarkIndices = extras.fullBody
    ? [0, 11, 12, 13, 14, 15, 16, 23, 24, 25, 26, 27, 28]
    : [11, 12, 13, 14, 15, 16, 23, 24];
  for (const index of landmarkIndices) {
    const point = pointFor(landmarks, index, width, height);
    if (!point) continue;
    context.beginPath();
    context.arc(point[0], point[1], 3.2, 0, Math.PI * 2);
    context.fill();
  }
  context.restore();
}

function drawFaceOverlay(
  canvas: HTMLCanvasElement,
  sample: TrackingSample | null,
  poseSample: PoseSample | null,
  overlayMode: OverlayMode,
  exportExtras: ExportExtras,
) {
  const context = canvas.getContext("2d");
  if (!context) return;

  const { width, height } = canvas;
  context.clearRect(0, 0, width, height);

  if (overlayMode === "off") return;

  if (!sample && !poseSample) {
    drawGuide(context, width, height);
    return;
  }

  drawPoseOverlay(context, poseSample, exportExtras, width, height);

  if (!sample) return;

  const points = sample.landmarks.map((landmark) => [
    landmark.x * width,
    landmark.y * height,
  ] as [number, number]);

  if (overlayMode === "mesh" && points.length > 2) {
    context.save();
    context.lineWidth = 0.78;
    context.strokeStyle = "rgba(91, 235, 191, 0.34)";

    for (const connection of FaceLandmarker.FACE_LANDMARKS_TESSELATION) {
      const start = points[connection.start];
      const end = points[connection.end];
      if (!start || !end) continue;
      context.beginPath();
      context.moveTo(start[0], start[1]);
      context.lineTo(end[0], end[1]);
      context.stroke();
    }

    context.lineWidth = 1.35;
    context.strokeStyle = "rgba(244, 216, 122, 0.78)";
    for (const connection of FaceLandmarker.FACE_LANDMARKS_CONTOURS) {
      const start = points[connection.start];
      const end = points[connection.end];
      if (!start || !end) continue;
      context.beginPath();
      context.moveTo(start[0], start[1]);
      context.lineTo(end[0], end[1]);
      context.stroke();
    }
    context.restore();
  }

  drawFaceExtras(context, points, exportExtras);

  if (overlayMode === "landmarks" || overlayMode === "mesh") {
    context.save();
    context.fillStyle = overlayMode === "mesh" ? "rgba(244, 216, 122, 0.78)" : "#5bebbfff";
    const radius = overlayMode === "mesh" ? 1.1 : 1.8;
    for (const point of points) {
      context.beginPath();
      context.arc(point[0], point[1], radius, 0, Math.PI * 2);
      context.fill();
    }
    context.restore();
  }

  const leftEye = points[33];
  const rightEye = points[263];
  const nose = points[1] ?? points[4];
  if (leftEye && rightEye && nose) {
    context.save();
    context.lineWidth = 2.4;
    context.lineCap = "round";
    context.strokeStyle = "rgba(244, 216, 122, 0.9)";
    context.beginPath();
    context.moveTo(leftEye[0], leftEye[1]);
    context.lineTo(rightEye[0], rightEye[1]);
    context.stroke();

    context.strokeStyle = "rgba(255, 111, 84, 0.86)";
    context.beginPath();
    context.moveTo(nose[0], nose[1]);
    context.lineTo(nose[0], nose[1] - Math.max(20, height * 0.055));
    context.stroke();
    context.restore();
  }
}

function StatusBadge({ status }: { status: CaptureStatus }) {
  const label = {
    loading: "Loading",
    idle: "Idle",
    starting: "Starting",
    ready: "Ready",
    recording: "Recording",
    stopped: "Captured",
    exporting: "Exporting",
    error: "Error",
  }[status];

  return <span className={`status-pill status-${status}`}>{label}</span>;
}

function App() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const landmarkerRef = useRef<FaceLandmarker | null>(null);
  const poseLandmarkerRef = useRef<PoseLandmarker | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const framesRef = useRef<CaptureFrame[]>([]);
  const statusRef = useRef<CaptureStatus>("loading");
  const startTimeRef = useRef(0);
  const lastVideoTimeRef = useRef(-1);
  const fpsTimesRef = useRef<number[]>([]);
  const displaySampleRef = useRef<TrackingSample | null>(null);
  const displayPoseSampleRef = useRef<PoseSample | null>(null);
  const lastTrackedAtRef = useRef(0);
  const lastPoseTrackedAtRef = useRef(0);
  const lastUiUpdateRef = useRef(0);
  const smoothingEnabledRef = useRef(true);
  const exportExtrasRef = useRef<ExportExtras>(DEFAULT_EXPORT_EXTRAS);

  const [status, setStatus] = useState<CaptureStatus>("loading");
  const [statusText, setStatusText] = useState("Loading face model");
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [overlayMode, setOverlayMode] = useState<OverlayMode>("mesh");
  const [tracking, setTracking] = useState(false);
  const [poseTracking, setPoseTracking] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [frameCount, setFrameCount] = useState(0);
  const [fpsEstimate, setFpsEstimate] = useState(0);
  const [lastSample, setLastSample] = useState<TrackingSample | null>(null);
  const [smoothingEnabled, setSmoothingEnabled] = useState(true);
  const [exportExtras, setExportExtras] = useState<ExportExtras>(DEFAULT_EXPORT_EXTRAS);
  const [error, setError] = useState("");

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    smoothingEnabledRef.current = smoothingEnabled;
    displaySampleRef.current = null;
    displayPoseSampleRef.current = null;
    lastTrackedAtRef.current = 0;
    lastPoseTrackedAtRef.current = 0;
  }, [smoothingEnabled]);

  useEffect(() => {
    exportExtrasRef.current = exportExtras;
    if (!needsPoseOverlay(exportExtras)) {
      displayPoseSampleRef.current = null;
      lastPoseTrackedAtRef.current = 0;
      setPoseTracking(false);
    }
  }, [exportExtras]);

  const setAppStatus = useCallback((nextStatus: CaptureStatus, nextText: string) => {
    statusRef.current = nextStatus;
    setStatus(nextStatus);
    setStatusText(nextText);
  }, []);

  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const allDevices = await navigator.mediaDevices.enumerateDevices();
    const cameras = allDevices.filter((device) => device.kind === "videoinput");
    setDevices(cameras);
    setSelectedDeviceId((current) => current || cameras[0]?.deviceId || "");
  }, []);

  const stopLoop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const resizeCanvas = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight) return false;

    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    }
    return true;
  }, []);

  const loop = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const landmarker = landmarkerRef.current;
    const poseLandmarker = poseLandmarkerRef.current;

    if (!video || !canvas || !landmarker) {
      rafRef.current = requestAnimationFrame(loop);
      return;
    }

    const canDraw = resizeCanvas();
    let drawSample = displaySampleRef.current;
    const timestamp = performance.now();

    if (canDraw && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      if (lastVideoTimeRef.current !== video.currentTime) {
        lastVideoTimeRef.current = video.currentTime;
        let sample: TrackingSample | null = null;
        let recordedPoseSample: PoseSample | null = null;
        try {
          sample = resultToSample(landmarker.detectForVideo(video, timestamp));
        } catch (detectError) {
          console.error(detectError);
          setError("Tracking failed. Restart the camera and try again.");
          setAppStatus("error", "Tracking failed");
        }

        if (poseLandmarker && needsPoseOverlay(exportExtrasRef.current)) {
          let poseSample: PoseSample | null = null;
          try {
            poseSample = resultToPoseSample(poseLandmarker.detectForVideo(video, timestamp));
          } catch (detectError) {
            console.error(detectError);
          }

          if (poseSample) {
            recordedPoseSample = poseSample;
            const nextDisplayPoseSample = displayPoseSample(
              displayPoseSampleRef.current,
              poseSample,
              smoothingEnabledRef.current,
            );
            displayPoseSampleRef.current = nextDisplayPoseSample;
            lastPoseTrackedAtRef.current = timestamp;
          } else if (timestamp - lastPoseTrackedAtRef.current > TRACKING_GRACE_MS) {
            displayPoseSampleRef.current = null;
          }
        } else {
          displayPoseSampleRef.current = null;
        }

        const times = fpsTimesRef.current;
        times.push(timestamp);
        while (times.length > 90) times.shift();

        if (sample) {
          const nextDisplaySample = displaySample(displaySampleRef.current, sample, smoothingEnabledRef.current);
          displaySampleRef.current = nextDisplaySample;
          drawSample = nextDisplaySample;
          lastTrackedAtRef.current = timestamp;
        } else if (timestamp - lastTrackedAtRef.current > TRACKING_GRACE_MS) {
          displaySampleRef.current = null;
          drawSample = null;
        }

        const shouldUpdateUi = timestamp - lastUiUpdateRef.current > UI_UPDATE_MS;
        if (shouldUpdateUi) {
          lastUiUpdateRef.current = timestamp;
          setFpsEstimate(averageFps(times));
          setTracking(Boolean(displaySampleRef.current));
          setPoseTracking(Boolean(displayPoseSampleRef.current));
          setLastSample(sample ?? displaySampleRef.current);
        }

        if (sample && statusRef.current === "recording") {
          const frame: CaptureFrame = {
            index: framesRef.current.length,
            timeMs: timestamp - startTimeRef.current,
            landmarks: cloneLandmarks(sample.landmarks),
            poseLandmarks: recordedPoseSample ? cloneLandmarks(recordedPoseSample.landmarks) : undefined,
            blendshapes: sample.blendshapes.map((blendshape) => ({ ...blendshape })),
            matrix: sample.matrix?.data ? [...sample.matrix.data] : [],
          };
          framesRef.current.push(frame);
          if (shouldUpdateUi) {
            setFrameCount(framesRef.current.length);
            setRecordingTime(frame.timeMs);
          }
        } else if (statusRef.current === "recording") {
          if (shouldUpdateUi) {
            setRecordingTime(timestamp - startTimeRef.current);
          }
        }
      }
    }

    if (canvas.width && canvas.height) {
      drawFaceOverlay(canvas, drawSample, displayPoseSampleRef.current, overlayMode, exportExtrasRef.current);
    }

    rafRef.current = requestAnimationFrame(loop);
  }, [overlayMode, resizeCanvas, setAppStatus]);

  const stopCamera = useCallback(() => {
    stopLoop();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    lastVideoTimeRef.current = -1;
    displaySampleRef.current = null;
    displayPoseSampleRef.current = null;
    lastTrackedAtRef.current = 0;
    lastPoseTrackedAtRef.current = 0;
    lastUiUpdateRef.current = 0;
    setTracking(false);
    setPoseTracking(false);
    setLastSample(null);
    setRecordingTime(0);
    if (statusRef.current !== "stopped") {
      setAppStatus("idle", "Camera stopped");
    }
  }, [setAppStatus, stopLoop]);

  useEffect(() => {
    let mounted = true;

    Promise.allSettled([createFaceLandmarker(), createPoseLandmarker()])
      .then(([faceResult, poseResult]) => {
        if (!mounted) {
          if (faceResult.status === "fulfilled") faceResult.value.close();
          if (poseResult.status === "fulfilled") poseResult.value.close();
          return;
        }

        if (faceResult.status === "fulfilled") {
          landmarkerRef.current = faceResult.value;
        } else {
          console.error(faceResult.reason);
          setError(faceResult.reason instanceof Error ? faceResult.reason.message : "Unable to load face model.");
          setAppStatus("error", "Model failed to load");
        }

        if (poseResult.status === "fulfilled") {
          poseLandmarkerRef.current = poseResult.value;
        } else {
          console.warn("Pose model failed to load; body overlays will be unavailable.", poseResult.reason);
        }

        if (faceResult.status === "fulfilled") {
          setAppStatus("idle", poseResult.status === "fulfilled" ? "Models ready" : "Face model ready");
          void refreshDevices();
        }
      });

    return () => {
      mounted = false;
      stopCamera();
      landmarkerRef.current?.close();
      landmarkerRef.current = null;
      poseLandmarkerRef.current?.close();
      poseLandmarkerRef.current = null;
    };
  }, [refreshDevices, setAppStatus, stopCamera]);

  const startCamera = useCallback(async () => {
    setError("");
    if (!landmarkerRef.current) {
      setAppStatus("loading", "Loading face model");
      return;
    }

    try {
      setAppStatus("starting", "Starting camera");
      stopLoop();
      streamRef.current?.getTracks().forEach((track) => track.stop());

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: selectedDeviceId ? undefined : "user",
        },
      });

      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) throw new Error("Video element unavailable.");
      video.srcObject = stream;
      await video.play();

      await refreshDevices();
      fpsTimesRef.current = [];
      lastVideoTimeRef.current = -1;
      displaySampleRef.current = null;
      displayPoseSampleRef.current = null;
      lastTrackedAtRef.current = 0;
      lastPoseTrackedAtRef.current = 0;
      lastUiUpdateRef.current = 0;
      setTracking(false);
      setPoseTracking(false);
      setAppStatus("ready", "Camera ready");
      rafRef.current = requestAnimationFrame(loop);
    } catch (cameraError: unknown) {
      console.error(cameraError);
      const message =
        cameraError instanceof Error
          ? cameraError.message
          : "Camera access failed. Check browser permissions.";
      setError(message);
      setAppStatus("error", "Camera unavailable");
      stopCamera();
    }
  }, [loop, refreshDevices, selectedDeviceId, setAppStatus, stopCamera, stopLoop]);

  const startRecording = useCallback(async () => {
    if (!streamRef.current) {
      await startCamera();
    }
    framesRef.current = [];
    startTimeRef.current = performance.now();
    setRecordingTime(0);
    setFrameCount(0);
    setError("");
    setAppStatus("recording", "Recording");
  }, [setAppStatus, startCamera]);

  const stopRecording = useCallback(() => {
    if (statusRef.current !== "recording") return;
    setRecordingTime(performance.now() - startTimeRef.current);
    setFrameCount(framesRef.current.length);
    setAppStatus("stopped", framesRef.current.length ? "Capture ready" : "No tracked frames");
  }, [setAppStatus]);

  const resetCapture = useCallback(() => {
    framesRef.current = [];
    setFrameCount(0);
    setRecordingTime(0);
    setError("");
    setAppStatus(streamRef.current ? "ready" : "idle", streamRef.current ? "Camera ready" : "Model ready");
  }, [setAppStatus]);

  const exportCapture = useCallback(async () => {
    if (!framesRef.current.length || !videoRef.current) return;
    try {
      setError("");
      setAppStatus("exporting", "Preparing C4D rig");
      const video = videoRef.current;
      const session: CaptureSession = {
        app: APP_NAME,
        version: APP_VERSION,
        createdAt: new Date().toISOString(),
        source: {
          width: video.videoWidth,
          height: video.videoHeight,
          fpsEstimate,
          mirroredPreview: true,
          previewSmoothing: smoothingEnabled,
        },
        frames: framesRef.current,
      };

      const blob = await buildCaptureZip(session, exportExtras);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      anchor.href = url;
      anchor.download = `tony-face-${stamp}.zip`;
      anchor.click();
      URL.revokeObjectURL(url);
      setAppStatus("stopped", "C4D rig package exported");
    } catch (exportError: unknown) {
      console.error(exportError);
      setError(exportError instanceof Error ? exportError.message : "Export failed.");
      setAppStatus("error", "Export failed");
    }
  }, [exportExtras, fpsEstimate, setAppStatus, smoothingEnabled]);

  const toggleExportExtra = useCallback((key: keyof ExportExtras) => {
    setExportExtras((current) => ({
      ...current,
      [key]: !current[key],
    }));
  }, []);

  const activeBlendshapes = useMemo(() => {
    return (lastSample?.blendshapes ?? [])
      .filter((item) => item.score > 0.08)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);
  }, [lastSample]);

  const selectedLabel = useMemo(() => {
    const device = devices.find((item) => item.deviceId === selectedDeviceId);
    return device?.label || "Default camera";
  }, [devices, selectedDeviceId]);

  const canRecord = status === "ready" || status === "stopped" || status === "idle";
  const hasFrames = frameCount > 0;

  return (
    <main className="app-shell">
      <section className="capture-workspace" aria-label="Face capture workspace">
        <div className="brand-row">
          <div>
            <p className="eyebrow">Tony Marghella</p>
            <h1>Face Capture</h1>
          </div>
          <StatusBadge status={status} />
        </div>

        <div className="camera-stage">
          <video ref={videoRef} className="camera-video" playsInline muted />
          <canvas ref={canvasRef} className="mesh-canvas" />
          <div className="stage-vignette" />
          <div className="stage-readout" aria-live="polite">
            <span className={tracking || poseTracking ? "dot dot-live" : "dot"} />
            <span>
              {tracking
                ? poseTracking
                  ? "Face + body locked"
                  : "Face locked"
                : poseTracking
                  ? "Body locked"
                  : statusText}
            </span>
          </div>
          {status === "recording" && <div className="recording-light">REC</div>}
        </div>
      </section>

      <aside className="control-rail" aria-label="Capture controls">
        <section className="panel">
          <div className="panel-heading">
            <Camera size={18} aria-hidden="true" />
            <h2>Camera</h2>
          </div>

          <label className="field">
            <span>Input</span>
            <select
              value={selectedDeviceId}
              onChange={(event) => setSelectedDeviceId(event.target.value)}
              disabled={status === "recording" || status === "starting"}
            >
              {devices.length ? (
                devices.map((device, index) => (
                  <option key={device.deviceId || index} value={device.deviceId}>
                    {device.label || `Camera ${index + 1}`}
                  </option>
                ))
              ) : (
                <option value="">Default camera</option>
              )}
            </select>
          </label>

          <div className="button-grid">
            <button type="button" className="button secondary" onClick={startCamera} disabled={status === "starting" || status === "recording"}>
              <Camera size={17} aria-hidden="true" />
              Start
            </button>
            <button type="button" className="icon-button" onClick={() => void refreshDevices()} aria-label="Refresh cameras" title="Refresh cameras">
              <RefreshCw size={18} aria-hidden="true" />
            </button>
          </div>

          <p className="microcopy">{selectedLabel}</p>
        </section>

        <section className="panel capture-panel">
          <div className="panel-heading">
            <Activity size={18} aria-hidden="true" />
            <h2>Capture</h2>
          </div>

          <div className="metrics">
            <div>
              <span>Time</span>
              <strong>{formatDuration(recordingTime)}</strong>
            </div>
            <div>
              <span>Frames</span>
              <strong>{frameCount}</strong>
            </div>
            <div>
              <span>FPS</span>
              <strong>{fpsEstimate ? fpsEstimate.toFixed(1) : "0.0"}</strong>
            </div>
          </div>

          <div className="record-actions">
            {status === "recording" ? (
              <button type="button" className="button danger" onClick={stopRecording}>
                <Square size={17} aria-hidden="true" />
                Stop
              </button>
            ) : (
              <button type="button" className="button primary" onClick={() => void startRecording()} disabled={!canRecord}>
                <Circle size={17} aria-hidden="true" />
                Record
              </button>
            )}
            <button type="button" className="icon-button" onClick={resetCapture} disabled={status === "recording"} aria-label="Reset capture" title="Reset capture">
              <RotateCcw size={18} aria-hidden="true" />
            </button>
          </div>
        </section>

        <section className="panel">
          <div className="panel-heading">
            <Eye size={18} aria-hidden="true" />
            <h2>Overlay</h2>
          </div>

          <div className="segmented-control" role="group" aria-label="Overlay mode">
            <button type="button" className={overlayMode === "mesh" ? "active" : ""} onClick={() => setOverlayMode("mesh")}>
              Mesh
            </button>
            <button type="button" className={overlayMode === "landmarks" ? "active" : ""} onClick={() => setOverlayMode("landmarks")}>
              Points
            </button>
            <button type="button" className={overlayMode === "off" ? "active" : ""} onClick={() => setOverlayMode("off")}>
              <EyeOff size={15} aria-hidden="true" />
            </button>
          </div>

          <label className="toggle-row">
            <input
              type="checkbox"
              checked={smoothingEnabled}
              onChange={(event) => setSmoothingEnabled(event.target.checked)}
            />
            <span>
              <strong>Smoothing</strong>
              <small>{smoothingEnabled ? "Stable preview, slight lag" : "Raw preview, lowest latency"}</small>
            </span>
          </label>
        </section>

        <section className="panel blendshape-panel">
          <div className="panel-heading">
            <Activity size={18} aria-hidden="true" />
            <h2>Blendshapes</h2>
          </div>

          <div className="blendshape-list">
            {activeBlendshapes.length ? (
              activeBlendshapes.map((item) => (
                <div className="blendshape-row" key={item.name}>
                  <span>{item.name}</span>
                  <div className="bar" aria-hidden="true">
                    <i style={{ width: `${Math.min(100, item.score * 100)}%` }} />
                  </div>
                  <strong>{formatScore(item.score)}</strong>
                </div>
              ))
            ) : (
              <p className="empty-state">{tracking ? "Neutral face" : "No face data"}</p>
            )}
          </div>
        </section>

        <section className="panel export-panel">
          <div className="panel-heading">
            <FileBox size={18} aria-hidden="true" />
            <h2>Export</h2>
          </div>

          <div className="checkbox-grid" aria-label="Tracked anatomy options">
            {EXPORT_EXTRA_LABELS.map((item) => (
              <label key={item.key} className="check-option">
                <input
                  type="checkbox"
                  checked={exportExtras[item.key]}
                  onChange={() => toggleExportExtra(item.key)}
                  disabled={status === "exporting"}
                />
                <span>{item.label}</span>
              </label>
            ))}
          </div>

          <button type="button" className="button export" onClick={() => void exportCapture()} disabled={!hasFrames || status === "recording" || status === "exporting"}>
            <Download size={17} aria-hidden="true" />
            Export C4D Rig Package
          </button>
          <p className="microcopy">Includes the C4D puppet-rig builder, raw capture data, FBX fallbacks, and CSV tracks.</p>
        </section>

        {error && <p className="error-text" role="alert">{error}</p>}
      </aside>
    </main>
  );
}

export default App;
